import CachedIcon from "@mui/icons-material/Cached";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip
} from "@mui/material";
import prettyBytes from "pretty-bytes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteFile, downloadFile, getFiles } from "../../api/file";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";
import { FileResponse } from "../../types/model/file";
import { formatTimestamp } from "../../utils";
import { FileUpload } from "./FileUpload";
import { useEntityAccessPermissions } from "../../hooks/Permissions/useEntityAccessPermissions";

interface FilesProps {
  entityId: number;
  managementUnitId?: number;
  captureId?: number;
  flowTestId?: number;
}

export default function Files({
  entityId,
  managementUnitId,
  captureId,
  flowTestId,
}: Readonly<FilesProps>) {
  const [loadingFiles, setLoadingFiles] = useState(false);

  const { t } = useTranslation();
  const handleError = useErrorHandler();

  const { canEditData } = useAccountPermissions();
  const {
    canEditEntity,
    isLoading: isLoadingEntityPermissions,
  } = useEntityAccessPermissions();

  const canEdit =
    canEditData &&
    !isLoadingEntityPermissions &&
    canEditEntity(entityId);
  
  const [search, setSearch] = useState("");
  const [filteredFiles, setFilteredFiles] = useState<FileResponse[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<FileResponse[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  const fetchExistingFiles = async () => {
    setLoadingFiles(true);
    try {
      const data = await getFiles(entityId, managementUnitId, captureId, flowTestId);
      setUploadedFiles(data);
      setFilteredFiles(data);
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleDownload = async (fileId: number, filename: string) => {
    try {
      await downloadFile(fileId, filename);
    } catch (error) {
      handleError(error);
    }
  };

  const handleDeleteExistingFile = async (fileId: number) => {
    setLoadingFiles(true);
    try {
      await deleteFile(fileId);
      await fetchExistingFiles();
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleOnFileUpdated = async () => {
    setLoadingFiles(true);
    try {
      await fetchExistingFiles();
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingFiles(false);
    }
  };

  useEffect(() => {
    const lower = search.toLowerCase();
    const filtered = uploadedFiles.filter((file) =>
      file.filename ? file.originalName?.toLowerCase().includes(lower) : false
    );
    setFilteredFiles(filtered);
    setPage(0);
  }, [search, uploadedFiles]);

  useEffect(() => {
    fetchExistingFiles();
  }, []);

  return (
    <>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <TextField
            fullWidth
            label={t("generic.search")}
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mr: 2 }}
          />
          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={fetchExistingFiles}>
              <CachedIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          {loadingFiles ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("generic.files.file-name")}</TableCell>
                  <TableCell>{t("generic.files.created")}</TableCell>
                  <TableCell>{t("generic.files.size")}</TableCell>
                  <TableCell align="right">
                    {t("generic.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredFiles
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((file) => (
                    <TableRow key={file.id}>
                      <TableCell>{file.originalName}</TableCell>
                      <TableCell>
                        {file.createdOn
                          ? formatTimestamp(file.createdOn.toString())
                          : ""}
                      </TableCell>
                      <TableCell>{prettyBytes(file.size)}</TableCell>

                      <TableCell align="right">
                        <IconButton
                          onClick={() =>
                            handleDownload(
                              file.id,
                              file.originalName ? file.originalName : ""
                            )
                          }
                          color="primary"
                          size="small"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        
                        {canEdit && (
                          <IconButton
                            onClick={() => handleDeleteExistingFile(file.id)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredFiles.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number.parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
      
      {canEdit && (
        <FileUpload
          entityId={entityId}
          managementUnitId={managementUnitId}
          captureId={captureId}
          flowTestId={flowTestId}
          onFilesUpdated={handleOnFileUpdated}
        />
      )}
    </>
  );
}